import { motion } from "framer-motion";
import { Clock } from "lucide-react";
import { closeDateLabel, pad, useCountdown, useDrop } from "./useDrop";

/** Slim fixed bar above the nav on the homepage. */
export const DropTicker = () => {
  const drop = useDrop();
  const c = useCountdown(drop?.closes_at);
  if (!drop) return <div className="fixed top-0 left-0 right-0 h-9 bg-[#0a0a0b] z-[60]" />;
  return (
    <div data-testid="drop-ticker" className="fixed top-0 left-0 right-0 h-9 bg-[#0a0a0b] text-white z-[60] flex items-center justify-center px-4 overflow-hidden">
      <p className="font-mono-d text-[10px] sm:text-[11px] uppercase tracking-[0.2em] flex items-center gap-2 sm:gap-3 whitespace-nowrap">
        <Clock size={12} className="text-[#ff2a85] shrink-0" />
        {c.closed ? (
          <span data-testid="drop-ticker-closed">Pre-orders closed · First Edition now in production</span>
        ) : (
          <>
            <span className="hidden sm:inline">First Edition pre-orders close in</span>
            <span className="sm:hidden">Closes in</span>
            <span data-testid="drop-ticker-time" className="text-[#ff2a85] tabular-nums">
              {c.days}d {pad(c.hours)}h {pad(c.minutes)}m {pad(c.seconds)}s
            </span>
            <span className="hidden md:inline text-white/50">· {closeDateLabel(drop)}</span>
          </>
        )}
      </p>
    </div>
  );
};

const Cell = ({ value, label, testId }) => (
  <div className="flex flex-col items-center min-w-[64px] sm:min-w-[84px]">
    <span data-testid={testId} className="font-display font-extrabold text-3xl sm:text-5xl leading-none tabular-nums">{pad(value)}</span>
    <span className="mt-2 font-mono-d text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a]">{label}</span>
  </div>
);

/** Bigger block for the hero. */
export const DropCountdown = () => {
  const drop = useDrop();
  const c = useCountdown(drop?.closes_at);
  if (!drop) return null;
  return (
    <motion.div data-testid="drop-countdown" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="inline-flex flex-col rounded-3xl border border-[#e3e2dc] bg-[#ffffff]/70 backdrop-blur-md px-6 py-5 sm:px-8 sm:py-6">
      <p className="font-mono-d text-[10px] uppercase tracking-[0.3em] text-[#ff2a85] flex items-center gap-2"><Clock size={11} /> {c.closed ? "Pre-orders closed" : "Pre-orders close in"}</p>
      {c.closed ? (
        <p data-testid="drop-countdown-closed" className="mt-3 font-display font-extrabold uppercase text-2xl sm:text-3xl leading-none">First Edition<br /><span className="text-pink">in production</span></p>
      ) : (
        <div className="mt-4 flex items-start gap-3 sm:gap-5">
          <Cell value={c.days} label="Days" testId="drop-days" />
          <span className="font-display font-extrabold text-3xl sm:text-5xl leading-none text-[#e3e2dc]">:</span>
          <Cell value={c.hours} label="Hours" testId="drop-hours" />
          <span className="font-display font-extrabold text-3xl sm:text-5xl leading-none text-[#e3e2dc]">:</span>
          <Cell value={c.minutes} label="Min" testId="drop-minutes" />
          <span className="font-display font-extrabold text-3xl sm:text-5xl leading-none text-[#e3e2dc] hidden sm:block">:</span>
          <div className="hidden sm:block"><Cell value={c.seconds} label="Sec" testId="drop-seconds" /></div>
        </div>
      )}
      <p className="mt-4 text-[11px] text-[#5f5e5a]">{c.closed ? "Thank you to every early believer." : `Closes ${closeDateLabel(drop)} · ships 12–16 weeks after close`}</p>
    </motion.div>
  );
};

/** Compact chip for the product quick view, next to the price. */
export const DropChip = () => {
  const drop = useDrop();
  const c = useCountdown(drop?.closes_at);
  if (!drop) return null;
  return (
    <span data-testid="drop-chip" className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono-d text-[9px] uppercase tracking-[0.2em] ${c.closed ? "bg-[#f3f2ec] text-[#5f5e5a]" : "bg-[#0a0a0b] text-white"}`}>
      <Clock size={10} className={c.closed ? "" : "text-[#ff2a85]"} />
      {c.closed ? "Pre-orders closed" : c.days > 0 ? `Closes in ${c.days}d ${c.hours}h` : `Closes in ${pad(c.hours)}:${pad(c.minutes)}:${pad(c.seconds)}`}
    </span>
  );
};
